import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';

interface Treffer {
  artikel: { id: string; sku: string; name: string; ean: string | null; active: boolean }[];
  container: { id: string; qr_token: string; iso_nummer: string | null; owner_name: string | null; status: string }[];
  lagerplaetze: { id: string; code: string; name: string; warehouse_code: string | null }[];
  partner: { id: string; number: string | null; name: string }[];
  auftraege: { id: string; number: string; reference: string | null; kind: string; status: string; owner_name: string | null }[];
}

interface Eintrag {
  key: string;
  gruppe: string;
  titel: string;
  unter: string;
  ziel: string;
  codes: string[]; // für exakten Scan-Treffer
}

const LEER: Treffer = { artikel: [], container: [], lagerplaetze: [], partner: [], auftraege: [] };

function flach(t: Treffer): Eintrag[] {
  const e: Eintrag[] = [];
  for (const a of t.artikel)
    e.push({
      key: `a-${a.id}`,
      gruppe: 'Artikel',
      titel: `${a.sku} · ${a.name}`,
      unter: a.active ? (a.ean ?? '') : 'inaktiv',
      ziel: `/artikel?q=${encodeURIComponent(a.sku)}`,
      codes: [a.sku, a.ean ?? ''].filter(Boolean),
    });
  for (const c of t.container)
    e.push({
      key: `c-${c.id}`,
      gruppe: 'Container',
      titel: c.qr_token,
      unter: [c.iso_nummer, c.owner_name, c.status].filter(Boolean).join(' · '),
      ziel: `/container?code=${encodeURIComponent(c.qr_token)}`,
      codes: [c.qr_token, c.iso_nummer ?? ''].filter(Boolean),
    });
  for (const l of t.lagerplaetze)
    e.push({
      key: `l-${l.id}`,
      gruppe: 'Lagerplatz',
      titel: l.code,
      unter: [l.warehouse_code, l.name].filter(Boolean).join(' · '),
      ziel: `/lagerstruktur?q=${encodeURIComponent(l.code)}`,
      codes: [l.code],
    });
  for (const p of t.partner)
    e.push({
      key: `p-${p.id}`,
      gruppe: 'Partner',
      titel: p.name,
      unter: p.number ?? '',
      ziel: `/partner?q=${encodeURIComponent(p.number || p.name)}`,
      codes: [p.number ?? ''].filter(Boolean),
    });
  for (const o of t.auftraege)
    e.push({
      key: `o-${o.id}`,
      gruppe: 'Auftrag',
      titel: `${o.number}${o.reference ? ` · ${o.reference}` : ''}`,
      unter: [o.kind, o.status, o.owner_name].filter(Boolean).join(' · '),
      ziel: `/auftraege?open=${encodeURIComponent(o.id)}`,
      codes: [o.number, o.reference ?? ''].filter(Boolean),
    });
  return e;
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [eintraege, setEintraege] = useState<Eintrag[]>([]);
  const [offen, setOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const letzteAnfrage = useRef(0);

  useEffect(() => {
    const code = q.trim();
    if (code.length < 2) {
      setEintraege([]);
      return;
    }
    const nr = ++letzteAnfrage.current;
    setBusy(true);
    const t = setTimeout(() => {
      api<Treffer>('/api/search', { query: { q: code } })
        .then((r) => {
          if (nr !== letzteAnfrage.current) return; // veraltete Antwort verwerfen
          setEintraege(flach(r));
          setOffen(true);
        })
        .catch(() => {
          if (nr === letzteAnfrage.current) setEintraege([]);
        })
        .finally(() => {
          if (nr === letzteAnfrage.current) setBusy(false);
        });
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  // Klick außerhalb schließt die Liste.
  useEffect(() => {
    function aus(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOffen(false);
    }
    document.addEventListener('mousedown', aus);
    return () => document.removeEventListener('mousedown', aus);
  }, []);

  function springe(ziel: string) {
    setOffen(false);
    setQ('');
    setEintraege([]);
    navigate(ziel);
  }

  function beiEnter() {
    const code = q.trim().toLowerCase();
    if (!code) return;
    // Handscanner: exakter Code-Treffer hat Vorrang - direkt dorthin springen.
    const exakt = eintraege.find((e) => e.codes.some((c) => c.toLowerCase() === code));
    const ziel = exakt ?? eintraege[0];
    if (ziel) springe(ziel.ziel);
  }

  // Für die Gruppenüberschriften die Reihenfolge festhalten.
  const gruppen = ['Artikel', 'Container', 'Lagerplatz', 'Partner', 'Auftrag'];

  return (
    <div className="globalsuche" ref={wrapRef}>
      <input
        type="search"
        value={q}
        placeholder="Suchen oder scannen …"
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => eintraege.length > 0 && setOffen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            beiEnter();
          } else if (e.key === 'Escape') {
            setOffen(false);
          }
        }}
      />
      {offen && q.trim().length >= 2 && (
        <div className="globalsuche-pop">
          {eintraege.length === 0 ? (
            <div className="globalsuche-leer">{busy ? 'Suche …' : 'Nichts gefunden'}</div>
          ) : (
            gruppen.map((g) => {
              const teil = eintraege.filter((e) => e.gruppe === g);
              if (teil.length === 0) return null;
              return (
                <div key={g}>
                  <div className="globalsuche-gruppe">{g}</div>
                  {teil.map((e) => (
                    <button key={e.key} className="globalsuche-eintrag" onClick={() => springe(e.ziel)}>
                      <span className="globalsuche-titel">{e.titel}</span>
                      {e.unter && <span className="globalsuche-unter">{e.unter}</span>}
                    </button>
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
